import { SHADE_KEYS, generateShadeScale, pickContrastInk } from './shade-scale';
import { DEFAULT_THEME, ShadeScale, Theme } from './theme.types';

const PRIMARY_PREFIX = '--p-primary-';
/** API `primary_color` ladder — Freightify's outlined / stroke colour (`--secondary` on :root). */
const BRAND_PREFIX = '--app-brand-';
const DARK_MODE_CLASS = 'app-dark';

/**
 * Applies tenant colours to the host document.
 *
 * **Freightify-web parity** (`freightify-web/apps/platform-host/src/app/layout/layout.component.ts`):
 *   - `--primary` on `:root` = API **secondary** (accent) — drives solid `.p-button`
 *     backgrounds via the PrimeNG `--p-primary-*` ladder.
 *   - `--secondary` on `:root` = API **primary** (navy) — drives default **outlined**
 *     buttons via `apps/platform/src/styles.scss` (`primeng-overrides` layer).
 *
 * PrimeNG Aura still reads `--p-primary-*` for menus, focus rings, highlights,
 * links, etc., so those controls inherit the **accent** (API secondary), matching
 * how the legacy host used `$primary` for filled surfaces.
 */
export function applyTheme(theme: Theme, document: Document): void {
  const safeTheme = sanitiseTheme(theme);

  const root = document.documentElement;
  const accentPalette = generateShadeScale(safeTheme.secondaryColor);
  const brandPalette = generateShadeScale(safeTheme.primaryColor);

  writeScale(root, PRIMARY_PREFIX, accentPalette);
  writeScale(root, BRAND_PREFIX, brandPalette);

  const accent500 = accentPalette[500];
  const brand500 = brandPalette[500];

  root.style.setProperty('--p-primary-color', accent500);
  root.style.setProperty('--p-primary-contrast-color', pickContrastInk(accent500));

  // Host-level aliases consumed by `apps/platform/src/styles.scss` and any
  // feature code that mirrors the old `--primary` / `--secondary` contract.
  root.style.setProperty('--primary', accent500);
  root.style.setProperty('--secondary', brand500);
  root.style.setProperty('--primary-text-color', pickContrastInk(accent500));

  if (safeTheme.darkMode) {
    root.classList.add(DARK_MODE_CLASS);
  } else {
    root.classList.remove(DARK_MODE_CLASS);
  }
}

/**
 * Reverts the document to PrimeNG Aura's defaults. Useful when a tenant
 * session ends or a fallback is intentional.
 */
export function resetTheme(document: Document): void {
  const root = document.documentElement;
  for (const key of SHADE_KEYS) {
    root.style.removeProperty(`${PRIMARY_PREFIX}${key}`);
    root.style.removeProperty(`${BRAND_PREFIX}${key}`);
  }
  root.style.removeProperty('--p-primary-color');
  root.style.removeProperty('--p-primary-contrast-color');
  root.style.removeProperty('--primary');
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--primary-text-color');
  root.classList.remove(DARK_MODE_CLASS);
}

function writeScale(root: HTMLElement, prefix: string, scale: ShadeScale): void {
  for (const key of SHADE_KEYS) {
    root.style.setProperty(`${prefix}${key}`, scale[key]);
  }
}

/**
 * Falls back to the default theme when the API-provided values are missing,
 * empty, or fail hex validation. Keeps `applyTheme` total — never throws.
 */
function sanitiseTheme(theme: Theme): Theme {
  return {
    primaryColor: isHex(theme.primaryColor) ? theme.primaryColor : DEFAULT_THEME.primaryColor,
    secondaryColor: isHex(theme.secondaryColor) ? theme.secondaryColor : DEFAULT_THEME.secondaryColor,
    darkMode: !!theme.darkMode,
  };
}

function isHex(value: unknown): value is string {
  return typeof value === 'string' && /^#?[0-9a-f]{6}$/i.test(value.trim());
}
